/**
 * Production Action Bar
 *
 * Fixed bottom bar with primary action buttons.
 * States:
 * - No job selected: "Select a job to start"
 * - Job selected, not started: START button with operation selector
 * - Job running: PAUSE + COMPLETE buttons
 * - Job paused: RESUME + COMPLETE buttons
 *
 * Large touch targets (minimum 44px height)
 */

import { useState, useEffect } from 'react';

export default function ProductionActionBar({
  activeJob,
  selectedJob,
  selectedJobs, // Array of selected job numbers (for batch display)
  quote, // Quote context for getting operations
  activeSessions,
  onStart,
  onPause,
  onResume,
  onComplete,
  formatElapsed,
  workCenter,
  selectedWorkCenters, // Array of user's selected work centers
}) {
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [piecesFinished, setPiecesFinished] = useState('');
  const [selectedOperation, setSelectedOperation] = useState(null);
  const [showOperationPicker, setShowOperationPicker] = useState(false);

  // Determine current state
  const job = activeJob || selectedJob;
  const session = job ? (job.session || job.activeSession || activeSessions?.[job.jobId]) : null;
  const isRunning = session?.state === 'running';
  const isPaused = session?.state === 'paused';
  const isActive = isRunning || isPaused;

  // Get available operations for this job
  const getAvailableOperations = () => {
    if (!job) return [];
    // From new project view structure
    if (job.myOperations && job.myOperations.length > 0) {
      return job.myOperations;
    }
    // From old structure
    if (job.currentOperation) {
      return [job.currentOperation];
    }
    // Fallback to work center
    if (workCenter) {
      return [{ operationCode: workCenter, workCenters: [workCenter] }];
    }
    return [];
  };

  const availableOps = getAvailableOperations();

  // Set default operation when job changes
  useEffect(() => {
    if (availableOps.length > 0 && !selectedOperation) {
      setSelectedOperation(availableOps[0]);
    }
  }, [job?.jobNumber]);

  // Handle complete with pieces
  const handleComplete = () => {
    if (job) {
      const pieces = parseInt(piecesFinished, 10) || 0;
      onComplete(job.jobId, pieces);
      setShowCompleteModal(false);
      setPiecesFinished('');
    }
  };

  // Get operation code for starting
  const getOperationCode = () => {
    if (selectedOperation) return selectedOperation.operationCode;
    if (!job) return workCenter;
    // Try myOperations first (new structure)
    if (job.myOperations && job.myOperations.length > 0) {
      return job.myOperations[0].operationCode;
    }
    // Fall back to currentOperation (old structure)
    return job.currentOperation?.operationCode || workCenter;
  };

  return (
    <>
      {/* Fixed Action Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-slate-100 border-t border-gray-200 px-4 py-3 safe-area-inset-bottom">
        {!job ? (
          // No job selected
          <div className="text-center text-gray-500 py-3">
            Select a job to start tracking
          </div>
        ) : !isActive ? (
          // Job selected but not started
          <div className="flex items-center gap-4">
            <div className="flex-1 min-w-0">
              <div className="text-sm text-gray-500 truncate">
                {job.partDescription || job.description || job.jobNumber}
              </div>
              {/* Operation selector - show if multiple options */}
              {availableOps.length > 1 ? (
                <div className="relative">
                  <button
                    onClick={() => setShowOperationPicker(!showOperationPicker)}
                    className="flex items-center gap-1 text-xs text-indigo-400 hover:text-indigo-300"
                  >
                    <span>{getOperationCode()}</span>
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {showOperationPicker && (
                    <div className="absolute bottom-full left-0 mb-1 bg-gray-700 rounded-lg shadow-lg py-1 min-w-[120px] z-10">
                      {availableOps.map((op, idx) => (
                        <button
                          key={idx}
                          onClick={() => {
                            setSelectedOperation(op);
                            setShowOperationPicker(false);
                          }}
                          className={`w-full text-left px-3 py-1.5 text-sm hover:bg-gray-600 ${
                            selectedOperation?.operationCode === op.operationCode
                              ? 'text-indigo-400'
                              : 'text-gray-900'
                          }`}
                        >
                          {op.operationCode}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-xs text-gray-500">
                  {getOperationCode()}
                </div>
              )}
            </div>
            <button
              onClick={() => onStart(job, getOperationCode())}
              className="px-8 py-4 bg-green-600 hover:bg-green-700 text-gray-900 text-lg font-bold rounded-xl transition-colors min-w-[140px]"
            >
              START
            </button>
          </div>
        ) : (
          // Job running or paused
          <div className="flex items-center gap-3">
            {/* Timer Display */}
            <div className="flex-1 min-w-0">
              <div className={`text-3xl font-mono font-bold ${
                isRunning ? 'text-green-400' : 'text-yellow-400'
              }`}>
                {formatElapsed(session)}
              </div>
              <div className="text-sm text-gray-500 truncate">
                {job.partDescription || job.description || job.jobNumber}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2">
              {isRunning ? (
                <button
                  onClick={() => onPause(job.jobId)}
                  className="px-6 py-4 bg-yellow-600 hover:bg-yellow-700 text-gray-900 text-lg font-bold rounded-xl transition-colors"
                >
                  PAUSE
                </button>
              ) : (
                <button
                  onClick={() => onResume(job.jobId)}
                  className="px-6 py-4 bg-green-600 hover:bg-green-700 text-gray-900 text-lg font-bold rounded-xl transition-colors"
                >
                  RESUME
                </button>
              )}
              <button
                onClick={() => setShowCompleteModal(true)}
                className="px-6 py-4 bg-blue-600 hover:bg-blue-700 text-gray-900 text-lg font-bold rounded-xl transition-colors"
              >
                DONE
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Complete Modal */}
      {showCompleteModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-100 rounded-2xl p-6 w-full max-w-sm">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Complete Job</h3>

            <div className="mb-4">
              <p className="text-sm text-gray-500 mb-1">
                {job?.partDescription || job?.description || job?.jobNumber}
              </p>
              <p className="text-2xl font-mono font-bold text-green-400">
                {formatElapsed(session)}
              </p>
            </div>

            <div className="mb-6">
              <label className="block text-sm text-gray-500 mb-2">
                Pieces Finished (optional)
              </label>
              <input
                type="number"
                value={piecesFinished}
                onChange={(e) => setPiecesFinished(e.target.value)}
                placeholder={job?.quantity?.toString() || '0'}
                className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 text-lg text-center focus:outline-none focus:border-blue-500"
                autoFocus
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowCompleteModal(false);
                  setPiecesFinished('');
                }}
                className="flex-1 px-4 py-3 bg-gray-700 hover:bg-gray-600 text-gray-900 font-medium rounded-xl transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleComplete}
                className="flex-1 px-4 py-3 bg-green-600 hover:bg-green-700 text-gray-900 font-bold rounded-xl transition-colors"
              >
                Complete
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
